import { Modal, Button, Form } from 'react-bootstrap';
import React, { useState, useRef } from "react";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { toast } from "react-toastify";
import { postData } from "../../utility/Utility";

const EmailModal = ({
    showEmailModal,
    setShowEmailModal,
    selectedInquiry,
    onEmailSent,
}) => {
    const [emailContent, setEmailContent] = useState("");
    const [subject, setSubject] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [selectedMessageType, setSelectedMessageType] = useState("custom");
    const editorRef = useRef(null);

    // Custom configuration for CKEditor
    const editorConfiguration = {
        toolbar: {
            items: [
                'heading', '|',
                'bold', 'italic', 'underline', 'strikethrough', '|',
                'fontColor', 'fontBackgroundColor', '|',
                'bulletedList', 'numberedList', '|',
                'alignment', '|',
                'link', 'insertTable', 'blockQuote', '|',
                'undo', 'redo'
            ]
        },
        heading: {
            options: [
                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
            ]
        },
        link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://'
        },
        table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        },
        placeholder: 'Write your email content here...',
        removePlugins: ['Markdown']
    };

    const insertTemplate = (templateType) => {
        let template = "";

        switch (templateType) {
            case "welcome":
                template = `<h2>Welcome to Our Insurance Services</h2>
<p>Dear <strong>${selectedInquiry?.fullName || 'Customer'}</strong>,</p>

<p>Welcome to our insurance services! We're excited to help you find the perfect coverage for your needs.</p>

<p>Our team will review your inquiry and get back to you shortly with personalized options.</p>

<p><strong>What to expect:</strong></p>
<ul>
    <li>Personalized insurance recommendations</li>
    <li>Competitive pricing</li>
    <li>Expert guidance throughout the process</li>
</ul>

<p>Best regards,<br>
<strong>Insurance Team</strong></p>`;
                break;

            case "followup":
                template = `<h3>Follow-up on Your Insurance Inquiry</h3>
<p>Dear <strong>${selectedInquiry?.fullName || 'Customer'}</strong>,</p>

<p>Following up on your recent inquiry about our insurance services. We wanted to check if you have any additional questions or need further information about our offerings.</p>

<p><strong>We can help you with:</strong></p>
<ul>
    <li>Policy details and coverage options</li>
    <li>Premium calculations</li>
    <li>Claims process explanation</li>
    <li>Any other questions you may have</li>
</ul>

<p>Please don't hesitate to reach out to us - we're here to help!</p>

<p>Best regards,<br>
<strong>Insurance Team</strong></p>`;
                break;

            case "quote":
                template = `<h2>Your Personalized Insurance Quote</h2>
<p>Dear <strong>${selectedInquiry?.fullName || 'Customer'}</strong>,</p>

<p>Thank you for your interest in our insurance services. Here is your personalized quote:</p>

<div style="background: #f8f9fa; padding: 20px; border-radius: 8px; border-left: 4px solid #007bff;">
    <h4>Quote Details:</h4>
    <table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
        <tr>
            <td style="padding: 8px; border-bottom: 1px solid #dee2e6;"><strong>Coverage Type:</strong></td>
            <td style="padding: 8px; border-bottom: 1px solid #dee2e6;">[Specify coverage type]</td>
        </tr>
        <tr>
            <td style="padding: 8px; border-bottom: 1px solid #dee2e6;"><strong>Premium:</strong></td>
            <td style="padding: 8px; border-bottom: 1px solid #dee2e6;">[Amount]</td>
        </tr>
        <tr>
            <td style="padding: 8px; border-bottom: 1px solid #dee2e6;"><strong>Policy Duration:</strong></td>
            <td style="padding: 8px; border-bottom: 1px solid #dee2e6;">[Policy period]</td>
        </tr>
        <tr>
            <td style="padding: 8px;"><strong>Key Benefits:</strong></td>
            <td style="padding: 8px;">
                <ul>
                    <li>[Benefit 1]</li>
                    <li>[Benefit 2]</li>
                    <li>[Benefit 3]</li>
                </ul>
            </td>
        </tr>
    </table>
</div>

<p>Please review this quote and let us know if you'd like to proceed or if you have any questions.</p>

<p>Best regards,<br>
<strong>Insurance Team</strong></p>`;
                break;

            case "information":
                template = `<h3>Additional Information Request</h3>
<p>Dear <strong>${selectedInquiry?.fullName || 'Customer'}</strong>,</p>

<p>Thank you for your interest in our insurance services. We'd like to provide you with some additional information:</p>

<p><strong>Available Coverage Options:</strong></p>
<ul>
    <li><strong>Life Insurance:</strong> Comprehensive life coverage plans</li>
    <li><strong>Health Insurance:</strong> Medical and hospitalization coverage</li>
    <li><strong>Auto Insurance:</strong> Vehicle and liability protection</li>
    <li><strong>Home Insurance:</strong> Property and contents coverage</li>
</ul>

<p><strong>Why Choose Us?</strong></p>
<ul>
    <li>✅ Competitive premiums</li>
    <li>✅ Fast claims processing</li>
    <li>✅ 24/7 customer support</li>
    <li>✅ Flexible payment options</li>
</ul>

<p>Would you like to schedule a call to discuss your specific needs?</p>

<p>Best regards,<br>
<strong>Insurance Team</strong></p>`;
                break;

            case "thankyou":
                template = `<h2>Thank You for Your Business</h2>
<p>Dear <strong>${selectedInquiry?.fullName || 'Customer'}</strong>,</p>

<p>Thank you for choosing our insurance services. We appreciate your trust in us to protect what matters most to you.</p>

<p><strong>Next Steps:</strong></p>
<ol>
    <li>You will receive your policy documents within 2-3 business days</li>
    <li>Keep your policy number handy for future reference</li>
    <li>Download our mobile app for easy claims processing</li>
</ol>

<p>If you have any questions, our customer service team is available 24/7.</p>

<p>Best regards,<br>
<strong>Insurance Team</strong></p>`;
                break;

            default:
                template = "";
        }

        setEmailContent(template);
    };

    const sendEmail = async (e) => {
        e.preventDefault();
        if (!selectedInquiry || !emailContent.trim() || !subject.trim()) {
            toast.error("Please enter subject and email content");
            return;
        }

        setIsSending(true);
        try {
            const response = await postData("api/email/send-email", {
                to: selectedInquiry.email,
                subject: subject,
                html: emailContent,
                inquiryId: selectedInquiry._id,
            });

            if (response?.success) {
                toast.success(`Email sent successfully to ${selectedInquiry.fullName}`);
                setShowEmailModal(false);
                onEmailSent && onEmailSent(selectedInquiry._id);

                // Reset form
                setEmailContent("");
                setSelectedMessageType("custom");
            } else {
                toast.error(response?.message || "Failed to send email");
            }
        } catch (error) {
            console.error("Email send error:", error);
            toast.error("Error sending email");
        } finally {
            setIsSending(false);
        }
    };

    const handleClose = () => {
        setShowEmailModal(false);
        setEmailContent("");
        setSelectedMessageType("custom");
    };

    const getCharacterCount = () => {
        // Strip HTML tags for character count
        const text = emailContent.replace(/<[^>]*>/g, '');
        return text.length;
    };

    const getWordCount = () => {
        const text = emailContent.replace(/<[^>]*>/g, '');
        return text.trim().split(/\s+/).filter(word => word.length > 0).length;
    };


    const insertPlaceholder = (type) => {
        let placeholder = '';
        switch (type) {
            case 'name':
                placeholder = '{{customer_name}}';
                break;
            case 'date':
                placeholder = '{{current_date}}';
                break;
            case 'policy':
                placeholder = '{{policy_number}}';
                break;
            default:
                placeholder = '{{placeholder}}';
        }
        setEmailContent(prev => prev + placeholder);
    };

    return (
        <div
            className={`modal fade ${showEmailModal ? "show d-block" : ""}`}
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title">
                            <i className="fas fa-envelope me-2"></i>
                            Send Email 
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={handleClose}
                        ></button>
                    </div>


                    <form onSubmit={sendEmail}>
                    <div className="modal-body">
                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Send To:</label>
                                    <p className="fw-bold text-primary mb-1">{selectedInquiry?.email}</p>
                                    <small className="text-muted">Customer: {selectedInquiry?.fullName}</small>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Email Type:</label>
                                    <Form.Select
                                        value={selectedMessageType}
                                        onChange={(e) => setSelectedMessageType(e.target.value)}
                                    >
                                        <option value="custom">Custom Message</option>
                                        <option value="welcome">Welcome Email</option>
                                        <option value="followup">Follow-up</option>
                                        <option value="quote">Quote</option>
                                        <option value="information">Information</option>
                                        <option value="thankyou">Thank You</option>
                                    </Form.Select>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Quick Actions:</label>
                            <div className="d-flex gap-2 flex-wrap">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => insertTemplate("welcome")}
                                >
                                    <i className="fas fa-hand-wave me-1"></i>
                                    Welcome
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => insertTemplate("followup")}
                                >
                                    <i className="fas fa-sync-alt me-1"></i>
                                    Follow-up
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-info"
                                    onClick={() => insertTemplate("quote")}
                                >
                                    <i className="fas fa-file-invoice-dollar me-1"></i>
                                    Quote
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning"
                                    onClick={() => insertTemplate("information")}
                                >
                                    <i className="fas fa-info-circle me-1"></i>
                                    Information
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertTemplate("thankyou")}
                                >
                                    <i className="fas fa-thumbs-up me-1"></i>
                                    Thank You
                                </button>
                                <div className="dropdown">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-dark dropdown-toggle"
                                        data-bs-toggle="dropdown"
                                    >
                                        <i className="fas fa-magic me-1"></i>
                                        Placeholders
                                    </button>
                                    <ul className="dropdown-menu">
                                        <li>
                                            <button
                                                    type="button"
                                                className="dropdown-item"
                                                onClick={() => insertPlaceholder('name')}
                                            >
                                                Customer Name
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                onClick={() => insertPlaceholder('date')}
                                            >
                                                Current Date
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                onClick={() => insertPlaceholder('policy')}
                                            >
                                                Policy Number
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => setEmailContent("")}
                                >
                                    <i className="fas fa-trash me-1"></i>
                                    Clear
                                </button>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Subject:</label>
                            <input
                                type="text"
                                className="form-control fw-semibold"
                                value={subject}
                                required
                                placeholder='Enter subject'
                                onChange={(e) => setSubject(e.target.value)}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold mb-2">
                                Email Content:
                            </label>
                            <div className="ckeditor-container border rounded">
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={emailContent}
                                    onChange={(event, editor) => {
                                        const data = editor.getData();
                                        setEmailContent(data);
                                    }}
                                    config={editorConfiguration}
                                    onReady={editor => {
                                        editorRef.current = editor;
                                        console.log('CKEditor5 React Component is ready to use!', editor);
                                    }}
                                    onBlur={(event, editor) => {
                                        console.log('Blur.', editor);
                                    }}
                                    onFocus={(event, editor) => {
                                        console.log('Focus.', editor);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Character count and preview */}
                        <div className="row mt-4">
                            <div className="col-md-6">
                                <div className="text-muted small">
                                    <i className="fas fa-text-width me-1"></i>
                                    Characters: {getCharacterCount()} |
                                    Words: {getWordCount()}
                                    {emailContent && (
                                        <span className="ms-2">
                                            <i className="fas fa-check-circle text-success me-1"></i>
                                            Ready to send
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-6 text-end">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-info me-2"
                                    onClick={() => {
                                        const textContent = emailContent.replace(/<[^>]*>/g, '');
                                        navigator.clipboard.writeText(textContent);
                                        toast.success("Text content copied to clipboard!");
                                    }}
                                >
                                    <i className="fas fa-copy me-1"></i>
                                    Copy Text
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => {
                                        const previewWindow = window.open('', '_blank');
                                        previewWindow.document.write(`
                                            <html>
                                                <head>
                                                    <title>Email Preview - CKEditor</title>
                                                    <style>
                                                        body { 
                                                            font-family: Arial, sans-serif; 
                                                            padding: 20px; 
                                                            max-width: 800px; 
                                                            margin: 0 auto;
                                                            line-height: 1.6;
                                                        }
                                                        table {
                                                            width: 100%;
                                                            border-collapse: collapse;
                                                        }
                                                        th, td {
                                                            border: 1px solid #ddd;
                                                            padding: 8px;
                                                            text-align: left;
                                                        }
                                                        th {
                                                            background-color: #f2f2f2;
                                                        }
                                                    </style>
                                                </head>
                                                <body>
                                                    ${emailContent}
                                                </body>
                                            </html>
                                        `);
                                    }}
                                >
                                    <i className="fas fa-eye me-1"></i>
                                    Preview
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleClose}
                            disabled={isSending}
                        >
                            <i className="fas fa-times me-1"></i>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSending || !emailContent.trim()}
                            onClick={sendEmail}
                        >
                            {isSending ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-paper-plane me-1"></i>
                                    Send Email
                                </>
                            )}
                        </button>
                    </div>

                    </form>
                </div>
            </div>

         
        </div>
    );
};

export default EmailModal;